/**
 * HMAC-signed browser storage.
 *
 * Wraps localStorage / sessionStorage so that every value is signed with
 * HMAC-SHA256 before writing and verified on read. The signing key is a
 * **non-extractable** CryptoKey stored in IndexedDB, which means it cannot
 * be read or copied -- only the browser's Web Crypto API can use it.
 *
 * Stored format: JSON `{ "v": "<value>", "s": "<base64 signature>" }`
 *
 * If a legacy (unsigned, plain-string) value is found on read, it is
 * automatically migrated: signed, re-stored, and returned. After
 * migration all future reads are verified.
 */

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_NAME = 'secure-storage';
const DB_VERSION = 1;
const STORE_NAME = 'keys';
const KEY_ID = 'hmac-signing-key';

let cachedKey: CryptoKey | null = null;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function idbGet(db: IDBDatabase, key: string): Promise<CryptoKey | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as CryptoKey | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, key: string, value: CryptoKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Key management
// ---------------------------------------------------------------------------

async function getOrCreateKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;

  const db = await openDB();
  try {
    const existing = await idbGet(db, KEY_ID);
    if (existing) {
      cachedKey = existing;
      return existing;
    }

    const key = await crypto.subtle.generateKey(
      { name: 'HMAC', hash: 'SHA-256' },
      false, // non-extractable -- cannot be read back
      ['sign', 'verify'],
    );

    await idbPut(db, KEY_ID, key);
    cachedKey = key;
    return key;
  } finally {
    db.close();
  }
}

// ---------------------------------------------------------------------------
// Encoding helpers
// ---------------------------------------------------------------------------

function toBase64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function fromBase64(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ---------------------------------------------------------------------------
// Sign / verify
// ---------------------------------------------------------------------------

async function sign(value: string): Promise<string> {
  const key = await getOrCreateKey();
  const data = new TextEncoder().encode(value);
  const sig = await crypto.subtle.sign('HMAC', key, data);
  return toBase64(sig);
}

async function verify(value: string, signature: string): Promise<boolean> {
  try {
    const key = await getOrCreateKey();
    const data = new TextEncoder().encode(value);
    const sigBuf = fromBase64(signature);
    return await crypto.subtle.verify('HMAC', key, sigBuf, data);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Signed entry format
// ---------------------------------------------------------------------------

interface SignedEntry {
  readonly v: string;
  readonly s: string;
}

function isSignedEntry(obj: unknown): obj is SignedEntry {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as SignedEntry).v === 'string' &&
    typeof (obj as SignedEntry).s === 'string'
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Write a signed value to the given Storage backend.
 * The value is HMAC-signed before being persisted.
 */
export async function secureSetItem(
  storage: Storage,
  key: string,
  value: string,
): Promise<void> {
  const signature = await sign(value);
  const entry: SignedEntry = { v: value, s: signature };
  storage.setItem(key, JSON.stringify(entry));
}

/**
 * Read and verify a signed value from the given Storage backend.
 *
 * Returns `null` when:
 * - the key does not exist
 * - the stored value has been tampered with (bad signature)
 *
 * If a legacy unsigned plain-string value is found it is automatically
 * migrated (signed and re-stored) then returned.
 */
export async function secureGetItem(
  storage: Storage,
  key: string,
): Promise<string | null> {
  const raw = storage.getItem(key);
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (isSignedEntry(parsed)) {
      const valid = await verify(parsed.v, parsed.s);
      return valid ? parsed.v : null;
    }
  } catch {
    // Not valid JSON -- treat as legacy unsigned value and migrate.
    try {
      await secureSetItem(storage, key, raw);
      return raw;
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Remove a key from the given Storage backend.
 */
export function secureRemoveItem(storage: Storage, key: string): void {
  storage.removeItem(key);
}

/**
 * Synchronously read the *unsigned* value from localStorage.
 *
 * Use **only** for non-security-critical reads that must happen before the
 * async crypto layer is ready (e.g. the inline `<script>` in index.html
 * that prevents a theme flash). The React layer should always re-verify
 * via `secureGetItem` on mount.
 */
export function readUnsignedValue(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isSignedEntry(parsed)) return parsed.v;
    } catch {
      // Legacy plain-string format
      return raw;
    }
  } catch {
    // localStorage unavailable
  }
  return null;
}
